import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ListTodo, Plus, Loader2 } from "lucide-react";

const PRIO_DOT = { Low: "bg-slate-300", Medium: "bg-blue-400", High: "bg-rose-500" };

export const MyTodosCard = () => {
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({ open: 0, done: 0 });
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/me/tasks").then((r) => { setItems(r.data.items || []); setCounts({ open: r.data.open, done: r.data.done }); }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    setItems((s) => s.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)));
    try { await api.patch(`/me/tasks/${t.id}`, { done: !t.done }); load(); } catch { toast.error("Could not update"); load(); }
  };
  const add = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try { await api.post("/me/tasks", { title: title.trim(), priority: "Medium" }); setTitle(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };

  const open = (items || []).filter((t) => !t.done).slice(0, 6);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="my-todos-card">
      <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
        <ListTodo className="w-4 h-4 text-indigo-500" /> My Todos
      </h2>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">{counts.open} open · {counts.done} done · private to you</p>

      <div className="flex items-center gap-2 mb-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="Add a quick to-do…"
          data-testid="my-todos-add-input"
          className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100"
        />
        <button onClick={add} disabled={busy || !title.trim()} data-testid="my-todos-add-btn"
          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>

      {items === null ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : open.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-400" data-testid="my-todos-empty">Nothing open — you're all caught up.</div>
      ) : (
        <div className="space-y-1" data-testid="my-todos-list">
          {open.map((t) => (
            <label key={t.id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-50 cursor-pointer" data-testid={`my-todo-${t.id}`}>
              <input type="checkbox" checked={!!t.done} onChange={() => toggle(t)} className="w-4 h-4 accent-indigo-600 shrink-0" data-testid={`my-todo-check-${t.id}`} />
              <span className={`w-2 h-2 rounded-full shrink-0 ${PRIO_DOT[t.priority] || PRIO_DOT.Medium}`} />
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{t.title}</span>
              {t.due_date && <span className="text-[10px] text-slate-400 shrink-0">{t.due_date}</span>}
            </label>
          ))}
        </div>
      )}

      <Link to="/me/tasks" data-testid="my-todos-view-all" className="inline-block mt-4 text-sm font-semibold text-indigo-600 hover:text-indigo-700">Open My Tasks →</Link>
    </div>
  );
};
